import { ColumnState } from "ag-grid-community";
import { DateRange } from "react-day-picker";
import { create } from "zustand";

import {
	defaultTQC1060_R01HeaderDto,
	TQC1060_R01_COMP_CD,
	TQC1060_R01DetailDto,
	TQC1060_R01HeaderDto,
} from "./Dto_TQC1060_R01";

export interface TQC1060_R01Filter {
	compCd: string;
	dateRange: DateRange | undefined;
	inoutClass: string;
	customCd: string;
	customNm: string;
	itemCd: string;
	itemNm: string;
}

export const defaultTQC1060_R01Filter: TQC1060_R01Filter = {
	compCd: TQC1060_R01_COMP_CD,
	dateRange: {
		from: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
		to: new Date(),
	},
	inoutClass: "",
	customCd: "",
	customNm: "",
	itemCd: "",
	itemNm: "",
};

export interface TQC1060_R01Info {
	inspectorEmp: string;
	inspectorEmpNm: string;
	approvorEmpno: string;
	approvorEmpNm: string;
}

export const defaultTQC1060_R01Info: TQC1060_R01Info = {
	inspectorEmp: "",
	inspectorEmpNm: "",
	approvorEmpno: "",
	approvorEmpNm: "",
};

export interface Store_TQC1060_R01 {
	TQC1060_R01Filter: TQC1060_R01Filter;
	TQC1060_R01PastFilter: TQC1060_R01Filter;
	setTQC1060_R01Filter: (filter: Partial<TQC1060_R01Filter>) => void;
	setTQC1060_R01PastFilter: (filter: Partial<TQC1060_R01Filter>) => void;
	TQC1060_R01Info: TQC1060_R01Info;
	setTQC1060_R01Info: (info: Partial<TQC1060_R01Info>) => void;
	TQC1060_R01List: TQC1060_R01HeaderDto[];
	setTQC1060_R01List: (list: TQC1060_R01HeaderDto[]) => void;
	selectedTQC1060_R01: TQC1060_R01HeaderDto | undefined;
	setSelectedTQC1060_R01: (item: TQC1060_R01HeaderDto | undefined) => void;
	initSelectedTQC1060_R01: () => void;
	TQC1060_R01DetailList: TQC1060_R01DetailDto[];
	setTQC1060_R01DetailList: (list: TQC1060_R01DetailDto[]) => void;
	updateTQC1060_R01DetailList: (valueIndex: number, data: TQC1060_R01DetailDto) => void;
	originalTQC1060_R01DetailList: TQC1060_R01DetailDto[];
	setOriginalTQC1060_R01DetailList: (list: TQC1060_R01DetailDto[]) => void;
	allCount: number;
	setAllCount: (allCount: number) => void;
	columnState: ColumnState[] | null;
	setColumnState: (state: ColumnState[] | null) => void;
	detailColumnState: ColumnState[] | null;
	setDetailColumnState: (state: ColumnState[] | null) => void;
	scrollTop: number | null;
	setScrollTop: (top: number | null) => void;
	detailScrollTop: number | null;
	setDetailScrollTop: (top: number | null) => void;
	initData: () => void;
}

const withHeaderIndex = (list: TQC1060_R01HeaderDto[]) =>
	list.map((item, index) => ({
		...defaultTQC1060_R01HeaderDto,
		...item,
		valueIndex: index + 1,
	}));

const withDetailIndex = (list: TQC1060_R01DetailDto[]) =>
	list.map((item, index) => ({ ...item, valueIndex: index + 1, isModified: false }));

export const useTQC1060_R01Store = create<Store_TQC1060_R01>((set) => ({
	TQC1060_R01Filter: defaultTQC1060_R01Filter,
	TQC1060_R01PastFilter: defaultTQC1060_R01Filter,
	setTQC1060_R01Filter: (filter) =>
		set((state) => ({ TQC1060_R01Filter: { ...state.TQC1060_R01Filter, ...filter } })),
	setTQC1060_R01PastFilter: (filter) =>
		set((state) => ({
			TQC1060_R01PastFilter: { ...state.TQC1060_R01PastFilter, ...filter },
		})),
	TQC1060_R01Info: defaultTQC1060_R01Info,
	setTQC1060_R01Info: (info) =>
		set((state) => ({ TQC1060_R01Info: { ...state.TQC1060_R01Info, ...info } })),
	TQC1060_R01List: [],
	setTQC1060_R01List: (list) => set({ TQC1060_R01List: withHeaderIndex(list) }),
	selectedTQC1060_R01: undefined,
	setSelectedTQC1060_R01: (item) => set({ selectedTQC1060_R01: item }),
	initSelectedTQC1060_R01: () => set({ selectedTQC1060_R01: undefined }),
	TQC1060_R01DetailList: [],
	setTQC1060_R01DetailList: (list) =>
		set({ TQC1060_R01DetailList: withDetailIndex(list) }),
	updateTQC1060_R01DetailList: (valueIndex, data) =>
		set((state) => ({
			TQC1060_R01DetailList: state.TQC1060_R01DetailList.map((item) =>
				item.valueIndex === valueIndex ? data : item,
			),
		})),
	originalTQC1060_R01DetailList: [],
	setOriginalTQC1060_R01DetailList: (list) =>
		set({ originalTQC1060_R01DetailList: withDetailIndex(list) }),
	allCount: 0,
	setAllCount: (allCount) => set({ allCount }),
	columnState: null,
	setColumnState: (columnState) => set({ columnState }),
	detailColumnState: null,
	setDetailColumnState: (detailColumnState) => set({ detailColumnState }),
	scrollTop: null,
	setScrollTop: (scrollTop) => set({ scrollTop }),
	detailScrollTop: null,
	setDetailScrollTop: (detailScrollTop) => set({ detailScrollTop }),
	initData: () =>
		set({
			TQC1060_R01Filter: defaultTQC1060_R01Filter,
			TQC1060_R01PastFilter: defaultTQC1060_R01Filter,
			TQC1060_R01Info: defaultTQC1060_R01Info,
			TQC1060_R01List: [],
			selectedTQC1060_R01: undefined,
			TQC1060_R01DetailList: [],
			originalTQC1060_R01DetailList: [],
			allCount: 0,
			scrollTop: null,
			detailScrollTop: null,
		}),
}));
